import { useEffect, useState } from 'react';
import { IconCopy, IconEdit, IconLoader2, IconTrash } from '@tabler/icons-react';
import api from '../../lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import type { BrandOption } from './BrandMultiSelect';

/**
 * DEV-1949 — saved brand groups: reusable named brand selections.
 *
 * Every mutation refetches through `onChanged()` rather than patching a local
 * copy: a group is shared across every campaigns operator, so an optimistic
 * list would show this operator's edit while hiding a colleague's.
 */

export interface BrandGroup {
  id: string;
  name: string;
  brandIds: string[];
  brands: BrandOption[];
  /** Ids stored on the group that no longer exist in `available-mvnos`. */
  omittedBrandIds: string[];
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

const MAX_GROUP_NAME_LENGTH = 50;

const getApiErrorMessage = (err: any, fallback: string): string => (
  err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || fallback
);

const isDuplicateName = (
  name: string,
  groups: BrandGroup[],
  ignoreId?: string,
): boolean =>
  groups.some(
    (group) =>
      group.id !== ignoreId && group.name.trim().toLowerCase() === name.trim().toLowerCase(),
  );

/** Client-side name rules, mirroring what the API rejects. The server stays the
 * authority (it also owns the 409 across operators) — this only saves a
 * round-trip on the obvious cases. */
const nameError = (name: string, groups: BrandGroup[], ignoreId?: string): string | null => {
  if (!name.trim()) return 'A group name is required';
  if (name.trim().length > MAX_GROUP_NAME_LENGTH) {
    return `A group name is at most ${MAX_GROUP_NAME_LENGTH} characters`;
  }
  if (isDuplicateName(name, groups, ignoreId)) return `A group named "${name.trim()}" already exists`;
  return null;
};

function BrandCheckboxList({
  options,
  selectedIds,
  onToggle,
  disabled,
}: {
  options: BrandOption[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="max-h-56 overflow-y-auto space-y-1 border rounded-md p-2">
      {options.map((option) => (
        <label
          key={option.id}
          className="flex items-center gap-2 text-sm py-0.5 cursor-pointer"
        >
          <Checkbox
            checked={selectedIds.includes(option.id)}
            onCheckedChange={() => onToggle(option.id)}
            disabled={disabled}
          />
          <span className="flex-1">{option.label}</span>
        </label>
      ))}
      {options.length === 0 && (
        <p className="text-xs text-muted-foreground">No brands available.</p>
      )}
    </div>
  );
}

type EditState =
  | { mode: 'edit'; target: BrandGroup }
  | { mode: 'duplicate'; target: BrandGroup }
  | null;

export function BrandGroupsDialog({
  open,
  onOpenChange,
  groups,
  options,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: BrandGroup[];
  options: BrandOption[];
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<EditState>(null);
  const [deleteTarget, setDeleteTarget] = useState<BrandGroup | null>(null);
  const [name, setName] = useState('');
  const [brandIds, setBrandIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!open) {
      setEditing(null);
      setDeleteTarget(null);
      setFormError('');
    }
  }, [open]);

  const startEdit = (target: BrandGroup) => {
    setEditing({ mode: 'edit', target });
    setName(target.name);
    setBrandIds(target.brandIds);
    setFormError('');
  };

  const startDuplicate = (target: BrandGroup) => {
    setEditing({ mode: 'duplicate', target });
    setName(`${target.name} (copy)`);
    setBrandIds(target.brandIds);
    setFormError('');
  };

  const closeForm = () => {
    setEditing(null);
    setDeleteTarget(null);
    setFormError('');
  };

  const toggleBrand = (id: string) => {
    setBrandIds((current) =>
      current.includes(id) ? current.filter((existing) => existing !== id) : [...current, id],
    );
  };

  const handleSave = async () => {
    if (!editing) return;
    const ignoreId = editing.mode === 'edit' ? editing.target.id : undefined;
    const invalid = nameError(name, groups, ignoreId);
    if (invalid) { setFormError(invalid); return; }
    setSaving(true);
    setFormError('');
    try {
      const payload = { name: name.trim(), brandIds };
      if (editing.mode === 'edit') {
        await api.put(`/campaigns/brand-groups/${encodeURIComponent(editing.target.id)}`, payload);
      } else {
        await api.post('/campaigns/brand-groups', payload);
      }
      closeForm();
      onChanged();
    } catch (err: any) {
      setFormError(getApiErrorMessage(err, 'Failed to save brand group'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setFormError('');
    try {
      await api.delete(`/campaigns/brand-groups/${encodeURIComponent(deleteTarget.id)}`);
      closeForm();
      onChanged();
    } catch (err: any) {
      setFormError(getApiErrorMessage(err, 'Failed to delete brand group'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen && !saving) onOpenChange(false); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {editing
              ? editing.mode === 'edit' ? 'Edit Brand Group' : 'Duplicate Brand Group'
              : deleteTarget ? 'Delete Brand Group' : 'Brand Groups'}
          </DialogTitle>
          <DialogDescription>
            {editing || deleteTarget
              ? 'Groups are shared with every campaigns operator.'
              : 'Saved brand selections, shared with every campaigns operator.'}
          </DialogDescription>
        </DialogHeader>

        {deleteTarget && (
          <div className="space-y-4 py-2">
            <p className="text-sm">
              Delete the group '{deleteTarget.name}'? The {deleteTarget.brandIds.length} brands in
              it will not be affected.
            </p>
            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {editing && (
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="brand-group-name">Name</Label>
              <Input
                id="brand-group-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Southern brands"
                maxLength={MAX_GROUP_NAME_LENGTH}
                disabled={saving}
              />
            </div>
            <div>
              <Label className="mb-2 block">
                Brands ({brandIds.length} selected)
              </Label>
              <BrandCheckboxList
                options={options}
                selectedIds={brandIds}
                onToggle={toggleBrand}
                disabled={saving}
              />
            </div>
            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {!editing && !deleteTarget && (
          <div className="space-y-2 py-2">
            {groups.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No brand groups saved yet. Select brands in the picker and choose "Save as group".
              </p>
            )}
            {groups.map((group) => (
              <div
                key={group.id}
                className="flex items-center gap-2 rounded-md border border-border px-3 py-2"
              >
                <span className="flex-1 text-sm font-medium">{group.name}</span>
                <span className="text-xs text-muted-foreground">
                  {group.brandIds.length} brands
                </span>
                <Button variant="outline" size="icon-xs" onClick={() => startEdit(group)}>
                  <IconEdit size={14} />
                </Button>
                <Button variant="outline" size="icon-xs" onClick={() => startDuplicate(group)}>
                  <IconCopy size={14} />
                </Button>
                <Button
                  variant="outline"
                  size="icon-xs"
                  className="text-destructive"
                  onClick={() => { setDeleteTarget(group); setFormError(''); }}
                >
                  <IconTrash size={14} />
                </Button>
              </div>
            ))}
            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          {editing && (
            <>
              <Button variant="outline" onClick={closeForm} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <IconLoader2 size={16} className="animate-spin" /> : null}
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
          {deleteTarget && (
            <>
              <Button variant="outline" onClick={closeForm} disabled={saving}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete} disabled={saving}>
                {saving ? <IconLoader2 size={16} className="animate-spin" /> : <IconTrash size={16} />}
                {saving ? 'Deleting...' : 'Delete'}
              </Button>
            </>
          )}
          {!editing && !deleteTarget && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SaveGroupDialog({
  open,
  onOpenChange,
  selectedIds,
  groups,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  groups: BrandGroup[];
  onChanged: () => void;
}) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (open) {
      setName('');
      setFormError('');
    }
  }, [open]);

  const handleSave = async () => {
    const invalid = nameError(name, groups);
    if (invalid) { setFormError(invalid); return; }
    setSaving(true);
    setFormError('');
    try {
      await api.post('/campaigns/brand-groups', { name: name.trim(), brandIds: selectedIds });
      onOpenChange(false);
      onChanged();
    } catch (err: any) {
      setFormError(getApiErrorMessage(err, 'Failed to save brand group'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen && !saving) onOpenChange(false); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save Brand Group</DialogTitle>
          <DialogDescription>
            Save the current {selectedIds.length}-brand selection as a group.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="save-brand-group-name">Name</Label>
            <Input
              id="save-brand-group-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Southern brands"
              maxLength={MAX_GROUP_NAME_LENGTH}
              disabled={saving}
            />
          </div>
          {formError && (
            <Alert variant="destructive">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <IconLoader2 size={16} className="animate-spin" /> : null}
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
